/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Headphones, RefreshCw, PhoneCall, MessageSquare, Star } from "lucide-react";
import { useApp } from "../context/AppContext";
import { AgentHeader } from "../components/agent/AgentHeader";
import { AgentMetrics } from "../components/agent/AgentMetrics";
import { AgentCallsTable } from "../components/agent/AgentCallsTable";
import { AgentSmsList } from "../components/agent/AgentSmsList";
import { AgentQualityList } from "../components/agent/AgentQualityList";

const DAYS = [7, 30, 90];

export default function AgentDetail() {
  const { username } = useParams();
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const navigate = useNavigate();
  const [days, setDays] = useState(() => Number(localStorage.getItem("agent_detail_days")) || 30);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("calls");
  const [params, setParams] = useSearchParams();
  const rateId = params.get("rate");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/telephony/agents/${username}/detail`, { params: { days } }); setD(data); setErr(null); }
    catch (e) { setErr(e?.response?.status || 500); }
    setLoading(false);
  }, [api, username, days]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { localStorage.setItem("agent_detail_days", String(days)); }, [days]);
  // 评分提醒深链 ?rate=<callId>：切到质量页签并自动打开评分对话框；通话不在当前范围则放宽到 30 天
  useEffect(() => {
    if (!rateId || !d) return;
    if (d.calls.some((c) => c.id === rateId)) setTab("quality");
    else if (days < 30) setDays(30);
  }, [rateId, d]); // eslint-disable-line react-hooks/exhaustive-deps
  const clearRate = () => { params.delete("rate"); setParams(params, { replace: true }); };

  if (err) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="agent-detail-error">
        <Headphones className="h-10 w-10 text-slate-300 mb-3" />
        <p className="text-lg font-semibold text-slate-700">{err === 403 ? (zh ? "无权查看该坐席" : "Access denied") : err === 404 ? (zh ? "坐席不存在" : "Agent not found") : (zh ? "加载失败" : "Failed to load")}</p>
        <button data-testid="agent-detail-back" onClick={() => navigate("/agents")} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 mt-3 text-sm text-red-600 hover:underline">{zh ? "← 返回坐席看板" : "← Back to agent board"}</button>
      </div>
    );
  }
  if (!d) return <div className="h-64 bg-white rounded-xl border border-slate-200 animate-pulse" data-testid="agent-detail-loading" />;

  const TABS = [
    { k: "calls", icon: PhoneCall, label: zh ? "通话记录" : "Calls", n: d.calls.length },
    { k: "sms", icon: MessageSquare, label: zh ? "短信记录" : "SMS", n: d.sms.length },
    { k: "quality", icon: Star, label: zh ? "通话质量评价清单" : "Quality reviews", n: d.quality.length },
  ];
  return (
    <div className="space-y-4" data-testid="agent-detail-page" data-username={username}>
      <div className="flex items-center gap-2 flex-wrap">
        <button data-testid="agent-detail-back" onClick={() => navigate("/agents")} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" />{zh ? "坐席看板" : "Agent board"}</button>
        <span className="text-slate-300">/</span>
        <span className="text-sm font-semibold text-slate-800">{zh ? "坐席详情" : "Agent detail"}</span>
        <div className="flex-wrap md:flex-nowrap ml-auto flex items-center gap-2">
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5" data-testid="agent-days" data-value={days}>
            {DAYS.map((n) => (
              <button key={n} data-testid={`agent-days-${n}`} onClick={() => setDays(n)} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-2.5 py-1 text-xs font-medium rounded-md ${days === n ? "bg-white text-red-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`)}>{zh ? `近 ${n} 天` : `${n}d`}</button>
            ))}
          </div>
          <button data-testid="agent-detail-refresh" onClick={load} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-1.5">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />{zh ? "刷新" : "Refresh"}
          </button>
        </div>
      </div>

      <AgentHeader a={d.agent} callerId={d.callerId} zh={zh} />
      <AgentMetrics m={d.metrics} zh={zh} />

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex border-b border-slate-100 overflow-x-auto" data-testid="agent-tabs">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`agent-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`inline-flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.k ? "border-brand-500 text-brand-600" : "border-transparent text-slate-500 hover:text-slate-800"}`)}>
              <t.icon className="h-4 w-4" />{t.label}<span className="text-[11px] text-slate-400 bg-slate-100 rounded-full px-1.5">{t.n}</span>
            </button>
          ))}
        </div>
        {tab === "calls" && <AgentCallsTable calls={d.calls} zh={zh} />}
        {tab === "sms" && <AgentSmsList sms={d.sms} zh={zh} logSince={d.smsLogSince} />}
        {tab === "quality" && <AgentQualityList items={d.quality} allItems={d.calls} zh={zh} canRate={d.canRate} onChanged={load} autoRateId={rateId} onAutoRateDone={clearRate} />}
      </div>
    </div>
  );
}
